import { create } from "zustand";
import { persist } from "zustand/middleware";

interface User {
  id: string;
  email: string;
  name: string;
  role: "admin" | "user";
  permissions: string[];
}

interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setAuth: (token: string, user: User) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
  updateUser: (user: User) => void;

  // Permissions
  hasPermission: (permission: string) => boolean;
  unAuthorized: () => boolean;
  isAdmin: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,

      setAuth: (token: string, user: User) => {
        set({
          token,
          user,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      clearAuth: () => {
        set({
          token: null,
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      updateUser: (user: User) => {
        set({ user });
      },

      unAuthorized() {
        const { user } = get();
        return user?.permissions.length === 0;
      },

      hasPermission: (permission: string) => {
        const { user } = get();
        return user?.permissions?.includes(permission) || false;
      },

      isAdmin: () => {
        const { user } = get();
        return user?.role === "admin" || false;
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Permission constants
export const PERMISSIONS = {

  system: {
    label: "Hệ thống",
    permissions: {
      admin: {
        key: "system:admin",
        label: "Quản trị hệ thống",
      },
    },
  },
  users: {
    label: "Người dùng",
    permissions: {
      view: {
        key: "users:view",
        label: "Xem danh sách",
      },
      create: {
        key: "users:create",
        label: "Thêm mới",
      },
      update: {
        key: "users:update",
        label: "Cập nhật",
      },
      delete: {
        key: "users:delete",
        label: "Xoá",
      },
    },
  },

  bachelorDegrees: {
    label: "Bằng Đại học",
    permissions: {
      view: {
        key: "bachelor_degrees:view",
        label: "Xem danh sách",
      },
      create: {
        key: "bachelor_degrees:create",
        label: "Thêm mới",
      },
      update: {
        key: "bachelor_degrees:update",
        label: "Cập nhật",
      },
      delete: {
        key: "bachelor_degrees:delete",
        label: "Xoá",
      },
      import: {
        key: "bachelor_degrees:import",
        label: "Nhập dữ liệu",
      },
      export: {
        key: "bachelor_degrees:export",
        label: "Xuất dữ liệu",
      },
    },
  },


  masterDegrees: {
    label: "Bằng thạc sĩ",
    permissions: {
      view: {
        key: "master_degrees:view",
        label: "Xem danh sách",
      },
      create: {
        key: "master_degrees:create",
        label: "Thêm mới",
      },
      update: {
        key: "master_degrees:update",
        label: "Cập nhật",
      },
      delete: {
        key: "master_degrees:delete",
        label: "Xoá",
      },
      import: {
        key: "master_degrees:import",
        label: "Nhập dữ liệu",
      },
      export: {
        key: "master_degrees:export",
        label: "Xuất dữ liệu",
      },
    },
  },

  languageCertificates: {
    label: "Chứng chỉ ngoại ngữ",
    permissions: {
      view: {
        key: "language_certificates:view",
        label: "Xem danh sách",
      },
      create: {
        key: "language_certificates:create",
        label: "Thêm mới",
      },
      update: {
        key: "language_certificates:update",
        label: "Cập nhật",
      },
      delete: {
        key: "language_certificates:delete",
        label: "Xoá",
      },
      import: {
        key: "language_certificates:import",
        label: "Nhập dữ liệu",
      },
      export: {
        key: "language_certificates:export",
        label: "Xuất dữ liệu",
      },
    },
  },

  awards: {
    label: "Giải thưởng",
    permissions: {
      view: {
        key: "awards:view",
        label: "Xem danh sách",
      },
      create: {
        key: "awards:create",
        label: "Thêm mới",
      },
      update: {
        key: "awards:update",
        label: "Cập nhật",
      },
      delete: {
        key: "awards:delete",
        label: "Xoá",
      },
      import: {
        key: "awards:import",
        label: "Nhập dữ liệu",
      },
      export: {
        key: "awards:export",
        label: "Xuất dữ liệu",
      },
    },
  },

  scholarship: {
    label: "Khen thưởng - Chứng nhận",
    permissions: {
      view: {
        key: "scholarship:view",
        label: "Xem danh sách",
      },
      create: {
        key: "scholarship:create",
        label: "Thêm mới",
      },
      update: {
        key: "scholarship:update",
        label: "Cập nhật",
      },
      delete: {
        key: "scholarship:delete",
        label: "Xoá",
      },
      import: {
        key: "scholarship:import",
        label: "Nhập dữ liệu",
      },
      export: {
        key: "scholarship:export",
        label: "Xuất dữ liệu",
      },
    },
  },

  scholarshipTitles: {
    label: "Danh hiệu khen thưởng - chứng nhận",
    permissions: {
      view: {
        key: "scholarship_titles:view",
        label: "Xem danh sách",
      },
      create: {
        key: "scholarship_titles:create",
        label: "Thêm mới",
      },
      update: {
        key: "scholarship_titles:update",
        label: "Cập nhật",
      },
      delete: {
        key: "scholarship_titles:delete",
        label: "Xoá",
      },
    }
  },


} as const;

